import { NavLink, Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutGrid, FolderTree, PlusCircle, Layers, Star, Award, Building2, LogOut, ExternalLink, Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const NAV = [
  { to: "/admin", label: "All Work", icon: LayoutGrid, end: true },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/work/new", label: "Add Work", icon: PlusCircle },
  { to: "/admin/bulk", label: "Bulk Upload", icon: Layers },
  { to: "/admin/featured", label: "Featured Work", icon: Star },
  { to: "/admin/awards", label: "Awards", icon: Award },
  { to: "/admin/clients", label: "Clients", icon: Building2 },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-copper-600" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-parchment text-charcoal-900" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-parchment/90 backdrop-blur-xl border-b border-charcoal-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-lg font-semibold">Portfolio Studio</span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-copper-600 border border-copper-600/25 bg-copper-100 rounded-full px-2.5 py-0.5">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-600 hover:text-copper-600 px-3 py-2 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-600 hover:text-copper-600 px-3 py-2 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-56 shrink-0">
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-1" aria-label="Admin">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-charcoal-900 text-parchment"
                      : "text-charcoal-600 hover:bg-surface-white hover:text-charcoal-900 border border-transparent"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
